import { Tabs } from '@kandji-inc/nectar-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useHistory, useRouteMatch } from 'react-router-dom';
import useWhiteBg from 'src/features/util/hooks/use-white-bg';

type TabProps = {
  tabId: string;
  label: string;
  path?: string;
  component: React.ReactNode;
};

type RenderWithTabsProps = {
  tabs: {
    [key: string]: TabProps;
  };
  css?: {
    tabs?: React.CSSProperties;
    content?: React.CSSProperties;
    container?: React.CSSProperties;
  };
};

const RenderWithTabs = ({ tabs, css }: RenderWithTabsProps) => {
  const { push } = useHistory();
  const tabsRef = useRef<HTMLDivElement>();
  const [distanceOffset, setDistanceOffset] = useState(null);

  const getDistanceOffset = (el: HTMLElement) => {
    const { top: tabsTop } = el.getBoundingClientRect();
    const pageHeaderElement = document.querySelector('[data-page-header]');

    const headerHeight = pageHeaderElement
      ? pageHeaderElement.getBoundingClientRect().height
      : 0;

    return headerHeight + tabsTop;
  };

  useEffect(() => {
    const el = tabsRef.current;
    if (el) {
      setDistanceOffset(getDistanceOffset(el));
    }
  }, [tabsRef]);

  const getTabPath = useCallback(
    (tab: TabProps) => `${tab.path}/${tab.tabId}`,
    [],
  );

  const getActiveTab = useCallback(() => {
    const activeTab = Object.values(tabs).find((tab) => {
      return !!useRouteMatch(`/${getTabPath(tab)}`);
    });

    return activeTab?.tabId ?? Object.values(tabs)[0].tabId;
  }, [tabs, getTabPath]);

  const tabsCss = useMemo(() => {
    return {
      tabs: {
        ...css?.tabs,
      },
      content: {
        overflow: 'auto',
        height: `calc(100vh - ${distanceOffset}px)`,
        paddingTop: '$3',
        paddingLeft: '$5',
        paddingRight: '$5',
        ...css?.content,
      },
      container: {
        width: '100%',
        ...css?.container,
      },
    };
  }, [distanceOffset]);

  const tabItems = useMemo(() => {
    return Object.values(tabs).map((tab, index) => ({
      css: {
        ...(index === 0 && { marginLeft: '$5' }),
        ...tabsCss?.tabs,
      },
      tabId: tab.tabId,
      label: tab.label,
      onClick: () => tab.path && push(`/${tab.path}/${tab.tabId}`),
    }));
  }, [tabs, tabsCss, push]);

  const tabContents = useMemo(() => {
    return Object.values(tabs).map((tab) => (
      <Tabs.Content key={tab.tabId} tabId={tab.tabId} css={tabsCss?.content}>
        {tab.component}
      </Tabs.Content>
    ));
  }, [tabs, tabsCss]);

  return (
    <Tabs.Container
      defaultTabId={getActiveTab()}
      ref={tabsRef}
      tabs={tabItems}
      css={tabsCss?.container}
    >
      {tabContents}
    </Tabs.Container>
  );
};

export default RenderWithTabs;
